import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { api } from '../lib/api';
import { BACKEND_URL } from '../lib/backend';
import Navbar from '../components/Navbar';
import Inbox from '../components/Inbox';
import DisputeDialog from '../components/DisputeDialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, Clock, CheckCircle, XCircle, Download, 
  Bell, Package, Calendar, Star, MessageCircle, ChevronDown, ChevronUp,
  HandshakeIcon, DollarSign, CreditCard, Music, Trash2, Inbox as InboxIcon,
  AlertTriangle, Play, Pause, Volume2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { OrdersTab, NotificationsTab, ReviewDialog, OfferCheckoutConfirmDialog } from '../components/dashboard';

const statusConfig = {
  pending_payment: { label: 'Pending Payment', color: 'bg-yellow-500/10 text-yellow-500', icon: Clock, description: 'Complete payment to send your request to the artist.' },
  paid: { label: 'Awaiting Response', color: 'bg-blue-500/10 text-blue-500', icon: Clock, description: 'Payment received. Waiting for the artist to accept or deny your request.' },
  accepted: { label: 'In Progress', color: 'bg-purple-500/10 text-purple-500', icon: Package, description: 'The artist accepted! Your dubplate is being recorded.' },
  denied: { label: 'Denied', color: 'bg-red-500/10 text-red-500', icon: XCircle, description: 'Artist was unable to fulfill the request. A refund has been initiated.' },
  delivered: { label: 'Delivered', color: 'bg-lime/10 text-lime', icon: Download, description: 'Your dubplate is ready! Preview it below, then confirm to release payment.' },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500', icon: CheckCircle, description: 'Order completed. Thanks for using Tune4Tune!' },
  cancelled: { label: 'Cancelled', color: 'bg-gray-500/10 text-gray-500', icon: XCircle, description: 'This order was cancelled. A refund has been initiated.' },
  cancelled_expired: { label: 'Expired & Refunded', color: 'bg-orange-500/10 text-orange-500', icon: XCircle, description: 'The 45-day delivery window passed. A full refund has been initiated.' },
  refunded: { label: 'Refunded', color: 'bg-orange-500/10 text-orange-500', icon: XCircle, description: 'A refund has been processed for this order.' },
};

const offerStatusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-500/10 text-yellow-500', icon: Clock },
  countered: { label: 'Counter Offer', color: 'bg-purple-500/10 text-purple-500', icon: HandshakeIcon },
  accepted: { label: 'Accepted', color: 'bg-lime/10 text-lime', icon: CheckCircle },
  denied: { label: 'Denied', color: 'bg-red-500/10 text-red-500', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-gray-500/10 text-gray-500', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500', icon: CheckCircle },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [offers, setOffers] = useState([]);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'inbox');
  const deleteTimerRef = useRef(null);
  
  // Audio preview state
  const audioRef = useRef(null);
  const [previewingOrderId, setPreviewingOrderId] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  
  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [editingReview, setEditingReview] = useState(false);
  
  // Offer action states
  const [processingOfferId, setProcessingOfferId] = useState(null);
  
  // Offer checkout confirmation dialog
  const [offerCheckoutConfirmOpen, setOfferCheckoutConfirmOpen] = useState(false);
  const [selectedOfferForCheckout, setSelectedOfferForCheckout] = useState(null);

  // Dispute dialog state
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [selectedOrderForDispute, setSelectedOrderForDispute] = useState(null);

  // Intentionally [] — api/toast/setters are stable module-level or useState refs
  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, notificationsRes, offersRes, inboxRes] = await Promise.all([
        api.getMyOrders(),
        api.getNotifications(),
        api.getMyOffers().catch(() => ({ data: [] })),
        api.getInboxUnreadCount().catch(() => ({ data: { unread_count: 0 } }))
      ]);
      setOrders(ordersRes.data);
      setNotifications(notificationsRes.data);
      setOffers(offersRes.data || []);
      setInboxUnread(inboxRes.data?.unread_count || 0);
      window.dispatchEvent(new Event('refreshBadges'));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab !== searchParams.get('tab')) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const handleCompleteOrder = async (orderId) => {
    try {
      await api.completeOrder(orderId);
      toast.success('Order completed! Payment released to artist.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete order');
    }
  };

  const handleOpenReviewDialog = async (order, isEdit = false) => {
    setSelectedOrder(order);
    setEditingReview(isEdit);
    if (isEdit) {
      try {
        const res = await api.getOrderReview(order.id);
        if (res.data) {
          setReviewRating(res.data.rating);
          setReviewComment(res.data.comment);
        }
      } catch {
        setReviewRating(5);
        setReviewComment('');
      }
    } else {
      setReviewRating(5);
      setReviewComment('');
    }
    setReviewDialogOpen(true);
  };

  const handleSubmitReview = async () => {
    if (!selectedOrder || !reviewComment.trim()) {
      toast.error('Please write a comment');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await api.createReview(selectedOrder.id, reviewRating, reviewComment);
      toast.success(editingReview ? 'Review updated!' : 'Review submitted! Thank you for your feedback.');
      setReviewDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
      window.dispatchEvent(new Event('refreshBadges'));
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      window.dispatchEvent(new Event('refreshBadges'));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleDeleteNotification = (e, id) => {
    e.stopPropagation();
    const deleted = notifications.find(n => n.id === id);
    setNotifications(prev => prev.filter(n => n.id !== id));

    const timer = setTimeout(() => {
      api.deleteNotification(id).catch(() => {
        setNotifications(prev => [...prev, deleted].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        toast.error('Failed to delete notification');
      });
    }, 5000);

    toast('Notification deleted', {
      action: {
        label: 'Undo',
        onClick: () => {
          clearTimeout(timer);
          setNotifications(prev => [...prev, deleted].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
        }
      },
      duration: 5000,
    });
  };

  const handleDeleteAllNotifications = () => {
    const backup = [...notifications];
    setNotifications([]);

    if (deleteTimerRef.current) clearTimeout(deleteTimerRef.current);
    deleteTimerRef.current = setTimeout(() => {
      api.deleteAllNotifications().catch(() => {
        setNotifications(backup);
        toast.error('Failed to clear notifications');
      });
    }, 5000);

    toast('All notifications cleared', {
      action: {
        label: 'Undo',
        onClick: () => {
          clearTimeout(deleteTimerRef.current);
          setNotifications(backup);
        }
      },
      duration: 5000,
    });
  };

  const handleDownload = (orderId) => {
    const fullUrl = `${BACKEND_URL}/api/orders/${orderId}/download`;
    // Cookie is sent automatically with credentials
    fetch(fullUrl, { credentials: 'include' })
      .then(res => {
        if (!res.ok) throw new Error('Download not available');
        return res.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dubplate-${orderId}.mp3`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Download not available. Payment must be released first.'));
  };

  const togglePreview = (order) => {
    const fileUrl = `${BACKEND_URL}${order.delivery_file_url}`;
    if (previewingOrderId === order.id) {
      // Toggle play/pause for same order
      if (audioRef.current) {
        if (isPlaying) { audioRef.current.pause(); } else { audioRef.current.play(); }
        setIsPlaying(!isPlaying);
      }
    } else {
      // Switch to new order audio
      setPreviewingOrderId(order.id);
      setAudioProgress(0);
      setAudioDuration(0);
      setIsPlaying(false);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.src = fileUrl;
          audioRef.current.play();
          setIsPlaying(true);
        }
      }, 100);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Offer actions
  const handleAcceptCounter = async (offerId) => {
    setProcessingOfferId(offerId);
    try {
      await api.userAcceptCounter(offerId);
      toast.success('Counter-offer accepted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept counter-offer');
    } finally {
      setProcessingOfferId(null);
    }
  };

  const handleDeclineCounter = async (offerId) => {
    setProcessingOfferId(offerId);
    try {
      await api.userDeclineCounter(offerId);
      toast.success('Counter-offer declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline counter-offer');
    } finally {
      setProcessingOfferId(null);
    }
  };

  const handleCheckoutOfferClick = (offer) => {
    setSelectedOfferForCheckout(offer);
    setOfferCheckoutConfirmOpen(true);
  };

  const handleCheckoutOffer = async () => {
    if (!selectedOfferForCheckout) return;
    
    setProcessingOfferId(selectedOfferForCheckout.id);
    try {
      const response = await api.checkoutOffer(selectedOfferForCheckout.id);
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to checkout');
      setProcessingOfferId(null);
      setOfferCheckoutConfirmOpen(false);
    }
  };

  const handleCancelOffer = async (offerId) => {
    setProcessingOfferId(offerId);
    try {
      await api.cancelOffer(offerId);
      toast.success('Offer cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel offer');
    } finally {
      setProcessingOfferId(null);
    }
  };

  const handleRequestCancel = async (orderId) => {
    try {
      await api.requestCancelOrder(orderId);
      toast.success('Cancellation request sent to artist');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request cancellation');
    }
  };

  const renderStars = (rating, interactive = false, onSelect = null) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={`star-${i}`}
        className={`w-6 h-6 cursor-pointer transition-colors ${
          i < rating ? 'text-lime fill-lime' : 'text-[#333] hover:text-lime/50'
        }`}
        onClick={() => interactive && onSelect && onSelect(i + 1)}
      />
    ));
  };

  // Count pending offers for badge
  const pendingOffersCount = offers.filter(o => ['pending', 'countered', 'accepted'].includes(o.status)).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-lime" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />
      
      <div className="pt-24 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            {...fadeInUp}
            className="mb-8"
          >
            <h1 className="font-heading text-3xl font-bold mb-2">Welcome, {user?.name}</h1>
            <p className="text-[#A1A1AA]">Manage your dubplate orders and notifications</p>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="bg-surface border border-[#262626]">
              <TabsTrigger value="inbox" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-inbox">
                <InboxIcon className="w-4 h-4 mr-2" />
                Inbox
                {inboxUnread > 0 && (
                  <span className="ml-2 w-5 h-5 bg-lime text-black text-xs rounded-full flex items-center justify-center font-bold">
                    {inboxUnread}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="orders" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-orders">
                <Package className="w-4 h-4 mr-2" />
                My Orders
                {pendingOffersCount > 0 && (
                  <span className="ml-2 w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {pendingOffersCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="notifications" className="data-[state=active]:bg-lime data-[state=active]:text-black" data-testid="tab-notifications">
                <Bell className="w-4 h-4 mr-2" />
                Notifications
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <span className="ml-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {notifications.filter(n => !n.is_read).length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* INBOX TAB */}
            <TabsContent value="inbox">
              <Inbox isArtistView={false} onUnreadChange={(count) => setInboxUnread(count)} />
            </TabsContent>

            {/* ORDERS TAB */}
            <TabsContent value="orders">
              <OrdersTab
                orders={orders} offers={offers} statusConfig={statusConfig} offerStatusConfig={offerStatusConfig}
                processingOfferId={processingOfferId} HandshakeIcon={HandshakeIcon}
                handleCancelOffer={handleCancelOffer} handleAcceptCounter={handleAcceptCounter}
                handleDeclineCounter={handleDeclineCounter} handleCheckoutOfferClick={handleCheckoutOfferClick}
                handleCompleteOrder={handleCompleteOrder} handleDownload={handleDownload}
                handleOpenReviewDialog={handleOpenReviewDialog} handleRequestCancel={handleRequestCancel}
                togglePreview={togglePreview} previewingOrderId={previewingOrderId}
                isPlaying={isPlaying} audioProgress={audioProgress} audioDuration={audioDuration}
                formatTime={formatTime}
                setSelectedOrderForDispute={setSelectedOrderForDispute} setDisputeDialogOpen={setDisputeDialogOpen}
              />
            </TabsContent>

            {/* NOTIFICATIONS TAB */}
            <TabsContent value="notifications">
              <NotificationsTab
                notifications={notifications}
                handleMarkAllRead={handleMarkAllRead}
                handleDeleteAllNotifications={handleDeleteAllNotifications}
                handleMarkNotificationRead={handleMarkNotificationRead}
                handleDeleteNotification={handleDeleteNotification}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Review Dialog */}
      <ReviewDialog
        open={reviewDialogOpen}
        onOpenChange={setReviewDialogOpen}
        selectedOrder={selectedOrder}
        editingReview={editingReview}
        reviewRating={reviewRating}
        setReviewRating={setReviewRating}
        reviewComment={reviewComment}
        setReviewComment={setReviewComment}
        renderStars={renderStars}
        handleSubmitReview={handleSubmitReview}
        submittingReview={submittingReview}
      />

      {/* Offer Checkout Confirmation Dialog */}
      <OfferCheckoutConfirmDialog
        open={offerCheckoutConfirmOpen}
        onOpenChange={setOfferCheckoutConfirmOpen}
        selectedOfferForCheckout={selectedOfferForCheckout}
        handleCheckoutOffer={handleCheckoutOffer}
        processingOfferId={processingOfferId}
      />

      {/* Dispute Dialog */}
      <DisputeDialog
        open={disputeDialogOpen}
        onOpenChange={setDisputeDialogOpen}
        order={selectedOrderForDispute}
        onDisputeCreated={fetchData}
      />

      {/* Hidden audio element for dubplate preview */}
      <audio
        ref={audioRef}
        onTimeUpdate={() => {
          if (audioRef.current && audioDuration > 0) {
            setAudioProgress((audioRef.current.currentTime / audioDuration) * 100);
          }
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) setAudioDuration(audioRef.current.duration);
        }}
        onEnded={() => { setIsPlaying(false); setAudioProgress(0); }}
      />
    </div>
  );
}
